import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { alpha, Box, Tooltip } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

interface SteelGradeNamesProps {
  steelGradeNames: string[];
}

export const SteelGradeNames = ({ steelGradeNames }: SteelGradeNamesProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const handleWheel = (event: WheelEvent) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += event.deltaY;
      event.preventDefault();
    }
  };

  const scrollRight = useCallback(() => {
    scrollContainerRef.current?.scrollBy({ left: 80, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // preventDefault requires a non-passive wheel listener.
      container.addEventListener("wheel", handleWheel, { passive: false });

      return () => {
        container.removeEventListener("wheel", handleWheel);
      };
    }
  }, []);

  useEffect(() => {
    const checkOverflow = () => {
      const container = scrollContainerRef.current;
      if (container) {
        const hasHorizontalOverflow =
          container.scrollWidth > container.clientWidth;
        setHasOverflow(hasHorizontalOverflow);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [steelGradeNames]);

  if (!steelGradeNames.length) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        height: "24px",
      }}
    >
      <Tooltip title={steelGradeNames.join(", ")} arrow>
        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            gap: 0.5,
            overflowX: "auto",
            maxWidth: "250px",
            height: "100%",
            alignItems: "center",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/Edge
          }}
        >
          {steelGradeNames.map((name) => (
            <Box
              className="steel-grade-name"
              key={name}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                color: (theme) => theme.palette.secondary.main,
                borderRadius: "8px",
                px: 0.5,
                py: 0.05,
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                height: "20px",
              }}
            >
              {name}
            </Box>
          ))}
          {/* Keep the final grade clear of the overlaid 30px chevron region. */}
          <Box sx={{ minWidth: "30px", flexShrink: 0 }} />
        </Box>
      </Tooltip>
      {hasOverflow ? (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: "30px",
            background: (theme) =>
              `linear-gradient(to right, transparent, ${alpha(
                theme.palette.background.paper,
                0.9
              )})`,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
          onClick={scrollRight}
        >
          <ChevronRightRoundedIcon
            sx={{
              fontSize: 20,
              opacity: 0.85,
              color: (theme) => theme.palette.secondary.main,
              mr: -0.5,
              animation: "pulseRight 1s infinite ease-in-out",
              "@keyframes pulseRight": {
                "0%": {
                  transform: "translateX(-2px)",
                },
                "50%": {
                  transform: "translateX(0px)",
                },
                "100%": {
                  transform: "translateX(-2px)",
                },
              },
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};
